import {forwardRef, MutableRefObject, useCallback, useRef, useState} from "react";
import {VisionBoardNode, VisionBoardNodeId} from "../../../cdx-models/utils/extended-fields";
import {NodeBushEntry} from "../useVisionBoardData";
import Markdown from "../../../components/Markdown";
import {css, DSIconButton, DSIconPencil} from "@cdx/ds";
import {
  CdxLexicalEditor,
  useCdxEditorState,
} from "../../../components/RichTextarea/Lexical/LexicalRichTextProvider";
import {DSInputStyles} from "@cdx/ds/components/DSForm/DSInput.css";
import {TooltipForChild, useClickOutside, useEsc} from "@cdx/common";
import {useCanvasNode} from "./useCanvasNode";
import {VisionBoardBag} from "../canvas-utils";
import {msNextThemes} from "../../../pages/milestones/Timeline/Timeline.css";

type EditViewProps = {
  content: string;
  onClose: () => void;
  selectContent: any;
  node: NodeBushEntry;
};

const EditView = forwardRef<HTMLElement, EditViewProps>((props, ref) => {
  const {content, node, onClose, selectContent} = props;
  const betterRef = useCallback(
    (n: HTMLElement) => {
      if (!ref) return;
      const _ref = ref as (el: HTMLElement | null) => void;
      if (!n) {
        _ref(n);
      } else {
        _ref((n.children[0] as HTMLElement).children[0] as HTMLElement);
      }
    },
    [ref]
  );
  const contentField = useCdxEditorState({
    initialContent: () => ({type: "text", value: content, selectContent}),
    contentKey: "visionBoardTextNode",
  });

  const handleContentSave = () => {
    const next = contentField.getText();
    if (next !== content) {
      node.node.set("value", {content: contentField.getText()});
    }
    onClose();
  };

  useEsc(handleContentSave);

  const handlers = useClickOutside(handleContentSave);

  return (
    <CdxLexicalEditor
      value={contentField.state}
      onBlur={handleContentSave}
      autoFocus
      className={css(
        {cursor: "default", flex: "auto", overflow: "auto", width: "100%", height: "100%"},
        DSInputStyles.root,
        DSInputStyles.sharedBase,
        DSInputStyles.inputBase,
        DSInputStyles.size.md,
        DSInputStyles.applyPaddings
      )}
      onCmdEnter={handleContentSave}
      onMouseDown={(e: any) => {
        e.stopPropagation();
      }}
      ref={betterRef}
      {...handlers}
    />
  );
});

type ScrollRef = MutableRefObject<{x: boolean; y: boolean}>;

const createRef = ({
  outerRef,
  scrollRef,
}: Pick<WheelHandlerParams, "outerRef"> & {scrollRef: ScrollRef}) => {
  let unsub: null | (() => void) = null;
  return (n: HTMLElement | null) => {
    if (unsub) {
      unsub();
      unsub = null;
    }

    if (!n) return;

    const check = () => {
      let currentNode: HTMLElement | null = n;
      if (!outerRef.current) return;
      while (currentNode) {
        if (currentNode === outerRef.current) break;

        const scrollX = currentNode.scrollWidth > currentNode.clientWidth;
        const scrollY = currentNode.scrollHeight > currentNode.clientHeight;
        if (scrollX || scrollY) {
          scrollRef.current.x = scrollX;
          scrollRef.current.y = scrollY;
          return;
        }

        currentNode = currentNode.parentElement;
      }
      scrollRef.current.x = false;
      scrollRef.current.y = false;
    };

    requestAnimationFrame(check);
    const ro = new ResizeObserver(check);
    ro.observe(n);
    unsub = () => {
      ro.disconnect();
    };
  };
};

type WheelHandlerParams = {
  outerRef: MutableRefObject<HTMLDivElement | null>;
};

export const useCanvasWheelHandler = ({outerRef}: WheelHandlerParams) => {
  const scrollRef: ScrollRef = useRef({x: false, y: false});
  const [containerRef] = useState(() => createRef({outerRef, scrollRef}));
  const handleWheel = (e: React.WheelEvent) => {
    if (e.deltaX && scrollRef.current.x) e.stopPropagation();
    if (e.deltaY && scrollRef.current.y) e.stopPropagation();
  };
  return [containerRef, handleWheel] as const;
};

let shouldBeInEditModeWhenMounted: VisionBoardNodeId | null = null;

export const textNodeWasJustCreated = (id: VisionBoardNodeId) => {
  shouldBeInEditModeWhenMounted = id;
};

const TextWidgetMenuItems = ({
  mode,
  setMode,
}: {
  mode: "view" | "edit";
  setMode: (m: "view" | "edit") => void;
}) => {
  return (
    <TooltipForChild tooltip="Edit Text">
      <DSIconButton
        variant="secondary"
        icon={<DSIconPencil />}
        active={mode === "edit"}
        onClick={() => setMode(mode === "edit" ? "view" : "edit")}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      />
    </TooltipForChild>
  );
};

const getInitialView = (id: VisionBoardNodeId): "edit" | "view" => {
  if (id === shouldBeInEditModeWhenMounted) {
    shouldBeInEditModeWhenMounted = null;
    return "edit";
  }
  return "view";
};

type TextWidgetProps = {
  node: NodeBushEntry;
  nodeData: VisionBoardNode & {type: "text"};
  bag: VisionBoardBag;
};
const TextWidget = (props: TextWidgetProps) => {
  const {nodeData, node, bag} = props;
  const [mode, setMode] = useState<"view" | "edit">(() => getInitialView(node.id));
  const {isActive, elements, nodeRef, containerProps} = useCanvasNode({
    node,
    nodeData,
    bag,
    hasColorBg: true,
    menuItems: <TextWidgetMenuItems mode={mode} setMode={setMode} />,
    minDims: [160, 40],
  });
  const [selectContent, setSelectContent] = useState(null);
  const wasActiveRef = useRef(false);

  const [containerRef, handleWheel] = useCanvasWheelHandler({outerRef: nodeRef});

  const handleMouseDown = () => {
    wasActiveRef.current = isActive;
  };

  const handleContentClick = (e: any) => {
    if (!wasActiveRef.current) return;
    e.stopPropagation();
    setMode("edit");
    if (e && e.nativeEvent && e.nativeEvent.$cdxEvent && e.nativeEvent.$cdxEvent.position) {
      setSelectContent(e.nativeEvent.$cdxEvent.position);
    } else {
      setSelectContent(null);
    }
  };

  const color = nodeData.color;

  return (
    <div
      {...containerProps}
      onWheelCapture={handleWheel}
      ref={nodeRef}
      className={css(
        {
          rounded: 4,
          flex: "none",
          bg: "foreground",
          colorTheme: "gray750",
        },
        containerProps.className,
        color && msNextThemes[color]
      )}
    >
      {mode === "view" ? (
        <Markdown
          variant="onDark"
          className={css({
            pa: "16px",
            cursor: isActive ? "default" : undefined,
            flex: "auto",
            overflow: "auto",
            width: "100%",
            height: "100%",
          })}
          onClick={handleContentClick}
          onMouseDown={handleMouseDown}
          containerRef={isActive ? containerRef : undefined}
        >
          {nodeData.value.content}
        </Markdown>
      ) : (
        <EditView
          content={nodeData.value.content}
          onClose={() => setMode("view")}
          selectContent={selectContent}
          node={node}
          ref={isActive ? containerRef : undefined}
        />
      )}
      {elements}
    </div>
  );
};

export default TextWidget;
